<script setup>
// ============================================================================
// BonBoZ: bảng của trang Tổng đài. MỘT component cho CẢ HAI bảng — bảng theo
// nhân viên và bảng theo thời gian dùng chung đủ 12 cột, chỉ khác cột đầu.
// Viết hai lần là mở đường cho hai bảng trôi khỏi nhau.
//
// TRẠNG THÁI RỖNG có hình hộp và chữ "Trống", đúng như Pancake — bảng chỉ có
// đầu cột rồi hết thì người đọc không biết là chưa có dữ liệu hay là hỏng.
//
// BỐN CỘT CHƯA CÓ NGUỒN (thành công, thời lượng) vẫn hiện 0: chưa nối kênh
// thoại nào thì "0 cuộc thành công" là đúng chứ không phải bịa. Chú giải từng
// cột nói rõ ngày có tổng đài thì cần thêm gì — xem đầu `Bonboz::TongDaiBuilder`.
// ============================================================================
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { ngay } from 'dashboard/helper/dinhDangNgay';
import { dinhDangThoiLuong } from 'dashboard/helper/thoiLuong';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  dong: { type: Array, default: () => [] },
  // 'nhan_vien' | 'thoi_gian' — quyết định cột đầu tiên.
  kieu: { type: String, default: 'nhan_vien' },
  gom: { type: String, default: 'ngay' },
  dangTai: { type: Boolean, default: false },
});

const { t } = useI18n();

// `chua_co` = cột cần bản ghi cuộc gọi, thứ bản Chatwoot này chưa có.
// `thoi_luong` = giá trị tính bằng giây, hiện ra thành "1m 30s".
const COT = [
  { khoa: 'goi_den' },
  { khoa: 'goi_den_rieng', hoi: true },
  { khoa: 'goi_den_ok', chua_co: true },
  { khoa: 'goi_den_ok_rieng', hoi: true, chua_co: true },
  { khoa: 'tg_goi_den', thoi_luong: true, chua_co: true },
  { khoa: 'goi_di' },
  { khoa: 'goi_di_rieng', hoi: true },
  { khoa: 'goi_di_ok', chua_co: true },
  { khoa: 'so_dien_thoai', hoi: true },
  { khoa: 'tg_goi_di', thoi_luong: true, chua_co: true },
  { khoa: 'don_chot' },
  { khoa: 'ti_le_chot', ti_le: ['don_chot', 'goi_di_ok'] },
];

const nhanDong = d => {
  if (props.kieu === 'nhan_vien') return d.ten;
  return props.gom === 'gio'
    ? t('BONBOZ.TRANG_TK.KHUNG_GIO', { tu: d.moc, den: (d.moc + 1) % 24 })
    : ngay(d.moc * 1000, 'ngayDay');
};

// `null` khi mẫu bằng 0: "0%" nói rằng đã đo và ra không, còn ở đây là chưa có
// gì để đo. Hai chuyện khác nhau.
const oCua = (d, c) => {
  if (c.thoi_luong) return dinhDangThoiLuong(d[c.khoa] ?? 0);
  if (!c.ti_le) return d[c.khoa] ?? 0;
  const [tu, mau] = c.ti_le;
  const duoi = d[mau] ?? 0;
  return duoi ? `${Math.round(((d[tu] ?? 0) / duoi) * 1000) / 10}%` : null;
};

const hang = computed(() =>
  props.kieu === 'nhan_vien'
    ? props.dong
    : // Thời gian: mới nhất lên đầu ở tab ngày; 0h → 23h ở tab giờ.
      [...props.dong].sort((a, b) =>
        props.gom === 'gio' ? a.moc - b.moc : b.moc - a.moc
      )
);
</script>

<template>
  <section class="border rounded-xl border-n-weak bg-n-solid-2">
    <div
      v-if="dangTai"
      class="m-4 rounded-lg h-40 bg-n-alpha-2 animate-pulse"
    />

    <!-- Bảng tự cuộn ngang trong khung của nó; trang không bao giờ cuộn ngang.
         Cột đầu dính trái — mười hai cột số mà mất tên dòng thì mỗi con số là
         một câu đố. -->
    <div v-else class="overflow-auto max-h-[28rem]">
      <!-- BonBoZ: boc de cuon ngang. Bang nhieu cot hon be ngang man thi bi CAT
           cung chu khong cuon — tren dien thoai mat han cac cot ben phai. Hai man
           Cam xuc va Quang cao da boc dung tu truoc, day la lam not cho deu. -->
      <div class="overflow-x-auto">
        <table class="w-full text-sm border-collapse">
          <thead class="sticky top-0 z-10 bg-n-slate-2">
            <tr class="text-xs border-b border-n-weak text-n-slate-11">
              <th
                class="sticky left-0 z-10 px-3 py-3 font-medium w-36 bg-n-slate-2 text-start"
              >
                {{
                  $t(
                    `BONBOZ.TONG_DAI.COT_${kieu === 'nhan_vien' ? 'NGUOI' : 'LUC'}`
                  )
                }}
              </th>
              <th
                v-for="c in COT"
                :key="c.khoa"
                v-tooltip.top="$t(`BONBOZ.TONG_DAI.G_${c.khoa}`)"
                class="px-3 py-3 font-medium leading-snug align-middle text-center min-w-28"
              >
                <span class="inline-flex items-start gap-1">
                  {{ $t(`BONBOZ.TONG_DAI.C_${c.khoa}`) }}
                  <!-- Dấu hỏi vàng đúng chỗ Pancake đặt: cột nào có định nghĩa
                       dễ đọc nhầm thì đánh dấu, không phải cột nào cũng có. -->
                  <span
                    v-if="c.hoi"
                    class="block rounded-full i-lucide-circle-help size-3.5 shrink-0 text-n-amber-11"
                    aria-hidden="true"
                  />
                </span>
              </th>
            </tr>
          </thead>

          <tbody>
            <tr v-if="!hang.length">
              <td :colspan="COT.length + 1" class="py-14">
                <span class="flex flex-col items-center gap-2 text-n-slate-10">
                  <span
                    class="block i-lucide-inbox size-10"
                    aria-hidden="true"
                  />
                  <span class="text-sm">{{ $t('BONBOZ.TONG_DAI.TRONG') }}</span>
                </span>
              </td>
            </tr>
            <tr
              v-for="d in hang"
              :key="d.id ?? d.moc"
              class="border-b border-n-weak last:border-b-0 hover:bg-n-alpha-1"
            >
              <td
                class="sticky left-0 px-3 py-2 whitespace-nowrap bg-n-solid-2 text-n-slate-12"
              >
                <span v-if="kieu !== 'nhan_vien'">{{ nhanDong(d) }}</span>
                <span v-else class="flex items-center gap-2">
                  <Avatar :src="d.anh" :name="d.ten" :size="26" rounded-full />
                  <span class="truncate">{{ d.ten }}</span>
                </span>
              </td>
              <td
                v-for="c in COT"
                :key="c.khoa"
                class="px-3 py-2 tabular-nums text-center"
                :class="oCua(d, c) ? 'text-n-slate-12' : 'text-n-slate-10'"
              >
                {{ oCua(d, c) ?? '—' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>
